import { V, V2d } from "../Vector";
import { V3, V3d } from "../Vector3";

/** Returns the sum of all values */
export function sum(...values: number[]): number {
  return values.reduce((acc, val) => acc + val, 0);
}

/** Modulo operator for modular arithmetic */
export function mod(a: number, b: number): number {
  return ((a % b) + b) % b;
}

/** Make sure a value is >= zero */
export function clampUp(value: number) {
  return Math.max(value, 0);
}

/** Limit a value to be in a range. */
export function clamp(value: number, min = 0, max = 1): number {
  return Math.max(min, Math.min(max, value));
}

/** The smoothstep function between 0 and 1 */
export function smoothStep(t: number): number {
  t = clamp(t);
  return 3 * t ** 2 - 2 * t ** 3;
}

/** Smoothstep function between 0 and 1 */
export function smootherStep(t: number): number {
  t = clamp(t);
  return 6 * t ** 5 - 15 * t ** 4 + 10 * t ** 3;
}

/** Linear interpolation between a and b, where t is in [0, 1] */
export function lerp(a: number, b: number, t: number = 0.5): number {
  return (1 - t) * a + t * b;
}

/** Returns t such that lerp(a, b, t) = t */
export function invLerp(a: number, b: number, value: number): number {
  return (value - a) / (b - a);
}

/** Linear interpolation between two 2D vectors */
export function lerpV2d(
  a: ArrayLike<number>,
  b: ArrayLike<number>,
  t: number = 0.5,
): V2d {
  const x = lerp(a[0], b[0], t);
  const y = lerp(a[1], b[1], t);
  return V(x, y);
}

/** Linear interpolation between two 3D vectors */
export function lerpV3d(
  a: ArrayLike<number>,
  b: ArrayLike<number>,
  t: number = 0.5,
): V3d {
  const x = lerp(a[0], b[0], t);
  const y = lerp(a[1], b[1], t);
  const z = lerp(a[2], b[2], t);
  return V3(x, y, z);
}

export function lerpOrSnap(
  a: number,
  b: number,
  t: number = 0.5,
  threshold: number = 0.01,
): number {
  if (Math.abs(b - a) < threshold) {
    return b;
  }
  return lerp(a, b, t);
}

/** Normalizes an angle in radians to be in the range [-pi, pi] */
export function normalizeAngle(angle: number) {
  return mod(angle + Math.PI, Math.PI * 2) - Math.PI;
}

/** Return the difference between two angles */
export function angleDelta(a: number, b: number): number {
  return mod(b - a + Math.PI, Math.PI * 2) - Math.PI;
}

/** Converts degrees to radians */
export function degToRad(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/** Converts radians to degrees */
export function radToDeg(radians: number): number {
  return (radians * 180) / Math.PI;
}

/** Returns the result of reflecting an angle across the X axis. */
export function reflectX(theta: number): number {
  return normalizeAngle(Math.PI - theta);
}

/** Returns the result of reflecting an angle across the Y axis. */
export function reflectY(theta: number): number {
  return normalizeAngle(-theta);
}

/** Returns the result of reflecting an angle across the XY axis. */
export function reflectXY(theta: number): number {
  return normalizeAngle(theta - Math.PI);
}

/**
 * Converts polar coordinates (angle and radius) to a Cartesian vector.
 * @param theta - Angle in radians (0 = east, π/2 = north)
 * @param r - Radius/magnitude
 * @returns V2d vector in Cartesian coordinates
 */
export function polarToVec(theta: number, r: number): V2d {
  return V(r * Math.cos(theta), r * Math.sin(theta));
}

/** Checks if a polygon is oriented counter-clockwise */
export function isCCW(points: readonly V2d[]): boolean {
  let total = 0;
  for (let i = 0; i < points.length; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[(i + 1) % points.length];
    total += (x2 - x1) * (y2 + y1);
  }
  return total > 0;
}

/** Step from one number towards another with a maximum step size */
export function stepToward(from: number, to: number, stepSize: number): number {
  if (from < to) {
    return Math.min(from + stepSize, to);
  } else {
    return Math.max(from - stepSize, to);
  }
}
